import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api } from "../api/client.ts";
import type { Brand, Domain } from "../api/types.ts";
import { confirmAction, toast } from "../components/feedback.tsx";
import { BrandBadge, Card, Drawer, Empty, TableSkeleton } from "../components/ui.tsx";

/**
 * Brands.
 *
 * A brand is the unit consent is tracked against, so it is not cosmetic:
 * unsubscribing from one must not affect another, and every domain, list and
 * campaign belongs to exactly one.
 */
export function BrandsPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<Domain[]>("/config/domains"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/config/brands/${id}`),
    onSuccess: () => {
      toast("Brand deleted");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const refresh = () => {
    setAdding(false);
    setEditing(null);
    void qc.invalidateQueries({ queryKey: ["brands"] });
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Brands</h1>
        <p>
          Each brand has its own domains, lists and consent. Unsubscribing from one leaves the
          others untouched.
        </p>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <button type="button" className="btn" onClick={() => setAdding(true)}>
          + New brand
        </button>
      </div>

      {adding && (
        <Drawer title="New brand" onClose={() => setAdding(false)}>
          <Card>
            <BrandForm onDone={refresh} />
          </Card>
        </Drawer>
      )}

      <Card>
        {brands.isLoading ? (
          <TableSkeleton />
        ) : brands.data?.length === 0 ? (
          <Empty>
            No brands yet. Add one before configuring a domain.{" "}
            <button type="button" className="btn small" onClick={() => setAdding(true)}>
              + New brand
            </button>
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Brand</th>
                <th>Slug</th>
                <th className="num">Domains</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {brands.data?.map((brand) => {
                const owned = (domains.data ?? []).filter((d) => d.brandId === brand.id);
                return editing === brand.id ? (
                  <tr key={brand.id}>
                    <td colSpan={4}>
                      <BrandForm brand={brand} onDone={refresh} onCancel={() => setEditing(null)} />
                    </td>
                  </tr>
                ) : (
                  <tr key={brand.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <BrandBadge badge={brand.badge} color={brand.color} />
                        <span style={{ fontWeight: 600 }}>{brand.name}</span>
                      </div>
                    </td>
                    <td className="mono" style={{ color: "var(--gray-500)" }}>
                      {brand.slug}
                    </td>
                    <td className="num">{owned.length}</td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        className="btn ghost small"
                        onClick={() => setEditing(brand.id)}
                      >
                        Edit
                      </button>{" "}
                      <button
                        type="button"
                        className="btn ghost small"
                        disabled={remove.isPending}
                        onClick={async () => {
                          if (
                            await confirmAction({
                              title: `Delete ${brand.name}?`,
                              message:
                                "This is refused if it still has domains, lists or campaigns.",
                              confirmLabel: "Delete brand",
                              destructive: true,
                            })
                          )
                            remove.mutate(brand.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <p className="caveat">
        A brand cannot be deleted while anything still belongs to it. That is deliberate — removing
        one would otherwise take its domains, campaigns and the record of everything they sent.
      </p>
    </div>
  );
}

/** Used for both creating and editing; the slug is fixed once set. */
function BrandForm({
  brand,
  onDone,
  onCancel,
}: {
  brand?: Brand;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(brand?.name ?? "");
  const [slug, setSlug] = useState(brand?.slug ?? "");
  const [badge, setBadge] = useState(brand?.badge ?? "");
  const [color, setColor] = useState(brand?.color ?? "#262626");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      brand
        ? api.patch(`/config/brands/${brand.id}`, { name, badge, color })
        : api.post("/config/brands", { name, slug, badge, color }),
    onSuccess: () => {
      onDone();
      toast("Brand saved");
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <form
      className="card-body"
      style={{ background: "var(--gray-50)" }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      {error && <div className="error">{error}</div>}

      <div className="row">
        <div className="field">
          <label htmlFor={`bn-${brand?.id ?? "new"}`}>Name</label>
          <input
            id={`bn-${brand?.id ?? "new"}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Only while creating: changing a slug later would orphan
              // anything that referenced it.
              if (!brand) {
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, ""),
                );
                setBadge(
                  e.target.value
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((w) => w[0]?.toUpperCase() ?? "")
                    .join(""),
                );
              }
            }}
            required
          />
        </div>
        <div className="field">
          <label htmlFor={`bs-${brand?.id ?? "new"}`}>Slug</label>
          <input
            id={`bs-${brand?.id ?? "new"}`}
            className="mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={Boolean(brand)}
            required
          />
          {brand && <div className="hint">Fixed once the brand exists.</div>}
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor={`bb-${brand?.id ?? "new"}`}>Badge</label>
          <input
            id={`bb-${brand?.id ?? "new"}`}
            value={badge}
            maxLength={4}
            onChange={(e) => setBadge(e.target.value.toUpperCase())}
            required
          />
          <div className="hint">One or two letters, shown beside campaigns and domains.</div>
        </div>
        <div className="field">
          <label htmlFor={`bc-${brand?.id ?? "new"}`}>Colour</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              id={`bc-${brand?.id ?? "new"}`}
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
            <BrandBadge badge={badge || "??"} color={color} />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn small" type="submit" disabled={save.isPending}>
          {brand ? "Save changes" : "Create brand"}
        </button>
        {onCancel && (
          <button type="button" className="btn ghost small" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
